import { useCallback, useRef } from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { VideoPlayerEvent, video } from '@tracking';

interface UseYouTubeVideoPlaybackTrackingProps {
  src: string;
}

const COMPLETION_PERCENTAGE = 100;

const useYouTubeVideoPlaybackTracking = ({ src }: UseYouTubeVideoPlaybackTrackingProps) => {
  const { trackEvent } = useEventTracking();
  const percentagesTracked = useRef<Set<number>>(new Set());

  const handleVideoPlay = useCallback(
    ({ playedPercentage }: { playedPercentage: number }) => {
      trackEvent(
        video({
          videoUrl: src,
          playerEvent: VideoPlayerEvent.Started,
          playedPercentage,
        })
      );
    },
    [src, trackEvent]
  );

  const handleVideoPause = useCallback(
    ({ playedPercentage }: { playedPercentage: number }) => {
      if (playedPercentage === COMPLETION_PERCENTAGE) return;

      trackEvent(
        video({
          videoUrl: src,
          playerEvent: VideoPlayerEvent.Paused,
          playedPercentage,
        })
      );
    },
    [src, trackEvent]
  );

  const handleVideoEnd = useCallback(() => {
    trackEvent(
      video({
        videoUrl: src,
        playerEvent: VideoPlayerEvent.Completed,
        playedPercentage: COMPLETION_PERCENTAGE,
      })
    );
  }, [src, trackEvent]);

  const handleVideoTimeUpdate = useCallback(
    ({ playedPercentage }: { playedPercentage: number }) => {
      if (playedPercentage % 25 === 0 && !percentagesTracked.current.has(playedPercentage)) {
        percentagesTracked.current.add(playedPercentage);
        trackEvent(
          video({
            videoUrl: src,
            playerEvent: VideoPlayerEvent.Progress,
            playedPercentage,
          })
        );
      }
    },
    [src, trackEvent]
  );

  return {
    handleVideoEnd,
    handleVideoPause,
    handleVideoPlay,
    handleVideoTimeUpdate,
  };
};

export default useYouTubeVideoPlaybackTracking;
